"use client"

import { useState, useEffect } from "react"
import { useSearchParams, useRouter } from "next/navigation"
import { Logo } from "@/components/crystality/logo"
import { CheckoutStep } from "./checkout-step"
import { AccountStep } from "./account-step"
import { SuccessStep } from "./success-step"
import { NewsletterSignup } from "./newsletter-signup"
import { cn } from "@/lib/utils"
import Link from "next/link"
import { Loader2 } from "lucide-react"

type FlowStep = "checkout" | "account" | "success" | "error"

interface FlowState {
  step: FlowStep
  email: string
  planId: string
  licenseKey: string
  orderId?: string
}

export function GetStartedFlow() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [flowState, setFlowState] = useState<FlowState>({
    step: "checkout",
    email: "",
    planId: "",
    licenseKey: "",
    orderId: "",
  })
  const [isLoadingProgress, setIsLoadingProgress] = useState(true)
  const [isReactivating, setIsReactivating] = useState(false)
  const [registrationOpen, setRegistrationOpen] = useState(true)
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)

  // Check registration status
  useEffect(() => {
    const checkRegistration = async () => {
      try {
        const response = await fetch("/api/settings/public")
        if (response.ok) {
          const data = await response.json()
          setRegistrationOpen(data.registrationOpen !== false)
        }
      } catch (error) {
        console.error("Failed to check registration status:", error)
      } finally {
        setIsLoadingSettings(false)
      }
    }
    checkRegistration()
  }, [])

  // Load progress from URL params or saved progress
  useEffect(() => {
    const loadProgress = async () => {
      try {
        // Check if user is reactivating or resubscribing (logged in user)
        const reactivateParam = searchParams.get("reactivate") === "true"
        const resubscribeParam = searchParams.get("resubscribe") === "true"
        setIsReactivating(reactivateParam || resubscribeParam)
        
        // If reactivating or resubscribing, get user email from API
        if (reactivateParam || resubscribeParam) {
          try {
            const userResponse = await fetch("/api/auth/me")
            if (userResponse.ok) {
              const userData = await userResponse.json()
              if (userData.success && userData.user?.email) {
                setFlowState({
                  step: "checkout",
                  email: userData.user.email,
                  planId: "monthly",
                  licenseKey: "",
                  orderId: "",
                })
                setIsLoadingProgress(false)
                return
              }
            }
          } catch (error) {
            console.error("Failed to fetch user data:", error)
          }
          // Not signed in but had resubscribe/reactivate in URL — treat as normal get-started and clean URL
          setIsReactivating(false)
          const params = new URLSearchParams(searchParams.toString())
          params.delete("resubscribe")
          params.delete("reactivate")
          const newSearch = params.toString()
          const newUrl = newSearch ? `/get-started?${newSearch}` : "/get-started"
          router.replace(newUrl)
        }

        // Check URL parameters first (for returning users from email)
        const urlStep = searchParams.get("step")
        const urlEmail = searchParams.get("email")
        const urlOrderId = searchParams.get("orderId")

        if (urlStep === "account" && urlEmail && urlOrderId) {
          // User is returning from email link - fetch license key from database
          try {
            const licenseResponse = await fetch(
              `/api/get-started/retrieve-license?orderId=${encodeURIComponent(urlOrderId)}&email=${encodeURIComponent(urlEmail)}`
            )
            if (licenseResponse.ok) {
              const licenseData = await licenseResponse.json()
              if (licenseData.success) {
                // Check if account already created
                if (licenseData.accountCreated) {
                  // Redirect to sign in
                  window.location.href = "/sign-in?message=account-already-exists"
                  return
                }
                
                setFlowState({
                  step: "account",
                  email: urlEmail,
                  planId: licenseData.productId || "monthly",
                  licenseKey: licenseData.licenseKey || "",
                  orderId: urlOrderId,
                })
                setIsLoadingProgress(false)
                return
              }
            }
          } catch (error) {
            console.error("Failed to fetch license key:", error)
          }
          
          // Fallback: set state without license key (will be fetched in AccountStep)
          setFlowState({
            step: "account",
            email: urlEmail,
            planId: "monthly",
            licenseKey: "",
            orderId: urlOrderId,
          })
          setIsLoadingProgress(false)
          return
        }

        // Try to load saved progress
        if (urlEmail) {
          const response = await fetch(`/api/get-started/progress?email=${encodeURIComponent(urlEmail)}`)
          if (response.ok) {
            const data = await response.json()
            if (data.progress) {
              setFlowState({
                step: data.progress.step || "checkout",
                email: data.progress.email || urlEmail || "",
                planId: data.progress.planId || "monthly",
                licenseKey: data.progress.licenseKey || "",
                orderId: data.progress.orderId || "",
              })
              setIsLoadingProgress(false)
              return
            }
          }
        }

        // No saved progress, start fresh
        setIsLoadingProgress(false)
      } catch (error) {
        console.error("Failed to load progress:", error)
        setIsLoadingProgress(false)
      }
    }

    loadProgress()
  }, [searchParams])

  // Determine steps based on whether user is reactivating
  const steps = isReactivating
    ? [
        { id: "checkout", label: "Checkout", number: 1 },
        { id: "account", label: "Reactivate", number: 2 },
        { id: "success", label: "Complete", number: 3 },
      ]
    : [
        { id: "checkout", label: "Checkout", number: 1 },
        { id: "account", label: "Create Account", number: 2 },
        { id: "success", label: "Complete", number: 3 },
      ]

  const currentStepIndex = steps.findIndex((s) => s.id === flowState.step)

  const handleCheckoutComplete = async (email: string, licenseKey: string, passedOrderId?: string) => {
    // Use the orderId passed directly from CheckoutStep (most reliable).
    // Fall back to progress API or license DB lookup only if not provided.
    let orderId = passedOrderId || ""

    if (!orderId) {
      try {
        const response = await fetch(`/api/get-started/progress?email=${encodeURIComponent(email)}`)
        if (response.ok) {
          const data = await response.json()
          orderId = data.progress?.orderId || ""
        }
      } catch {
        // Non-critical — continue without
      }
    }

    if (!orderId && licenseKey) {
      try {
        const licenseResponse = await fetch(
          `/api/get-started/retrieve-license?email=${encodeURIComponent(email)}&licenseKey=${encodeURIComponent(licenseKey)}`
        )
        if (licenseResponse.ok) {
          const licenseData = await licenseResponse.json()
          orderId = licenseData.orderId || ""
          if (licenseData.licenseKey && licenseData.licenseKey !== licenseKey) {
            licenseKey = licenseData.licenseKey
          }
        }
      } catch {
        // Non-critical — continue without
      }
    }

    setFlowState((prev) => ({
      ...prev,
      step: "account",
      email,
      licenseKey: licenseKey || prev.licenseKey,
      orderId: orderId || prev.orderId,
    }))

    // Update URL without reload
    const urlParams = new URLSearchParams({
      step: "account",
      email,
      ...(orderId && { orderId }),
    })
    window.history.pushState({}, "", `/get-started?${urlParams.toString()}`)
  }

  const handleCheckoutError = () => {
    setFlowState((prev) => ({
      ...prev,
      step: "error",
    }))
  }

  const handleAccountCreated = () => {
    setFlowState((prev) => ({
      ...prev,
      step: "success",
    }))
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Logo size="md" href="https://crystality.win" />
      </header>

      {/* Progress Steps - Only show if registration is open or user is reactivating */}
      {(!isLoadingProgress && !isLoadingSettings) && (registrationOpen || isReactivating) && (
        <div className="w-full max-w-2xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between relative">
            {/* Progress Line - starts at right edge of first circle, ends at left edge of last circle */}
            {/* With 3 evenly distributed steps: first circle center at 16.67%, last at 83.33% */}
            {/* Line spans from first circle right (16.67% + 20px) to last circle left (83.33% - 20px) */}
            <div className="absolute top-5 h-0.5 bg-border z-0" style={{ 
              left: 'calc(16.666% + 1.25rem)', 
              width: 'calc(66.666% - 2.5rem)'
            }}>
              <div
                className="h-full bg-primary transition-all duration-500"
                style={{
                  width: currentStepIndex > 0 
                    ? `${(currentStepIndex / (steps.length - 1)) * 100}%`
                    : '0%',
                }}
              />
            </div>

            {steps.map((step, index) => (
              <div
                key={step.id}
                className="relative z-10 flex flex-col items-center flex-1"
              >
                <div
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 border-2 relative z-10",
                    index < currentStepIndex
                      ? "bg-primary border-primary text-primary-foreground"
                      : index === currentStepIndex
                      ? "bg-background border-primary text-primary glow-purple"
                      : "bg-background border-border text-muted-foreground"
                  )}
                >
                  {index < currentStepIndex ? (
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    step.number
                  )}
                </div>
                <span
                  className={cn(
                    "mt-2 text-xs font-medium",
                    index <= currentStepIndex
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          {isLoadingProgress || isLoadingSettings ? (
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
              <p className="mt-4 text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <>
              {/* Show newsletter signup if registration is closed and user is not reactivating */}
              {!registrationOpen && !isReactivating ? (
                <NewsletterSignup />
              ) : (
                <>
                  {flowState.step === "checkout" && (
                    <CheckoutStep
                      initialEmail={flowState.email}
                      onComplete={handleCheckoutComplete}
                      onError={handleCheckoutError}
                    />
                  )}
                  {flowState.step === "account" && (
                    <AccountStep
                      email={flowState.email}
                      licenseKey={flowState.licenseKey}
                      orderId={flowState.orderId}
                      onComplete={handleAccountCreated}
                    />
                  )}
                  {(flowState.step === "success" || flowState.step === "error") && (
                    <SuccessStep
                      type={flowState.step === "success" ? "success" : "error"}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="p-6 text-center">
        {!isReactivating && (
          <p className="text-xs text-muted-foreground">
            Already have an account?{" "}
            <Link href="/sign-in" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        )}
      </footer>
    </div>
  )
}
